// src/components/Dashboard.js
import React, { useEffect, useState } from 'react';
import RecurringExpensesList from './RecurringExpensesList';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  CssBaseline,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Grid,
  Paper,
  Button,
  FormControl,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  BottomNavigation,
  BottomNavigationAction,
  Tooltip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  ExitToApp as ExitToAppIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  BarChart as BarChartIcon,
  ListAlt as ListAltIcon,
  DarkModeOutlined as DarkModeIcon,
  LightModeOutlined as LightModeIcon,
  AddCircle as AddCircleIcon,
  History as HistoryIcon,
  HelpOutline as HelpOutlineIcon,
  Favorite as FavoriteIcon,
  Mail as MailIcon,
  EmojiEvents as EmojiEventsIcon,
  Security as SecurityIcon,
  Policy as PolicyIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { updateRecurringExpense, deleteRecurringExpense, updateExpense, fetchAllExpenses } from './DashboardExpenses';
import { handleLogout as logoutHandler } from './DashboardAuth';
import { fetchFamilies, handleCreateFamily, handleJoinFamily, handleLeaveFamily } from './DashboardFamily';
import GuideOverlay from './GuideOverlay';
import MessageCenter from './MessageCenter';
import AddExpense from './AddExpense';
import AddPastExpense from './AddPastExpense';
import Achievements from './Achievements';
import './Dashboard.css';
import 'react-datepicker/dist/react-datepicker.css';

const drawerWidth = 240;

const Dashboard = ({ mode, toggleMode }) => {
  // Stati
  const [userData, setUserData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [incompleteExpenses, setIncompleteExpenses] = useState([]);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const [userFamily, setUserFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [bottomNavValue, setBottomNavValue] = useState('dashboard');
  const [achievementsOpen, setAchievementsOpen] = useState(false);

  // Stati per le dialog/modal
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [inviteCodeDialogOpen, setInviteCodeDialogOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [openPastExpense, setOpenPastExpense] = useState(false);
  const [openMessageCenter, setOpenMessageCenter] = useState(false);

  // Stato per spesa selezionata (per completare i dettagli)
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [updatedName, setUpdatedName] = useState('');
  const [updatedCategory, setUpdatedCategory] = useState('');

  // Stato per overlay di guida
  const [showGuideOverlay, setShowGuideOverlay] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();

  const handleOpenAchievements = () => {
    setAchievementsOpen(true);
  };

  const handleCloseAchievements = () => {
    setAchievementsOpen(false);
  };

  // Carica le categorie
  const fetchCategories = async () => {
    try {
      const categoriesSnapshot = await getDocs(collection(db, 'categories'));
      const categoriesData = categoriesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoriesData);
    } catch (err) {
      console.error('Errore nel caricamento delle categorie:', err);
      setError('Errore nel caricamento delle categorie');
    }
  };

  // Autenticazione e caricamento famiglie
  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserData({
          name: user.displayName,
          email: user.email,
          picture: user.photoURL,
        });
        try {
          await fetchFamilies(user.uid, setFamilies);
        } catch (err) {
          console.error('Errore nel recupero delle famiglie:', err);
          setError('Errore nel recupero delle famiglie');
        }
        if (families.length > 0 && !userFamily) {
          setUserFamily(families[0]);
        }
        setLoading(false);
      } else {
        navigate('/');
      }
    });
    return () => {
      unsubscribeAuth();
    };
  }, [navigate, families.length, userFamily]);

  // Imposta la prima famiglia se disponibile
  useEffect(() => {
    if (families.length > 0 && !userFamily) {
      setUserFamily(families[0]);
    }
  }, [families, userFamily]);

  // Integrazione in tempo reale: usiamo subscribeToAllExpenses per aggiornare le spese in tempo reale
  useEffect(() => {
    if (userFamily) {
      const userId = auth.currentUser.uid;
      // La funzione subscribeToAllExpenses restituisce una funzione di cleanup
      const unsubscribe = require('./DashboardExpenses').subscribeToAllExpenses(
        userId,
        userFamily,
        setExpenses,
        setRecurringExpenses,
        setIncompleteExpenses
      );
      return () => {
        unsubscribe();
      };
    }
  }, [userFamily]);

  // Carica le categorie
  useEffect(() => {
    fetchCategories();
  }, []);

  // Mostra overlay di guida al primo accesso
  useEffect(() => {
    const guideSeen = localStorage.getItem('guideSeen');
    if (!guideSeen) {
      setShowGuideOverlay(true);
    }
  }, []);

  const handleHideGuideOverlay = () => {
    localStorage.setItem('guideSeen', 'true');
    setShowGuideOverlay(false);
  };

  const handleShowGuideOverlay = () => {
    setShowGuideOverlay(true);
  };

  // Gestione Drawer (mobile)
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Gestione menu utente (apertura e logout)
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Aggiorna i dettagli di una spesa incompleta
  const handleUpdateExpenseDetails = async () => {
    if (!selectedExpense || !updatedName || !updatedCategory) {
      alert('Compila tutti i campi per aggiornare la spesa.');
      return;
    }
    try {
      await updateExpense(selectedExpense.id, updatedName, updatedCategory);
      setSelectedExpense(null);
      setUpdatedName('');
      setUpdatedCategory('');
    } catch (err) {
      console.error("Errore nell'aggiornamento della spesa:", err);
      alert("Errore nell'aggiornamento della spesa.");
    }
  };

  // Funzioni per la gestione della famiglia
  const handleCreateNewFamily = async () => {
    if (auth.currentUser) {
      await handleCreateFamily(setFamilies, setUserFamily);
    }
  };

  const handleJoinExistingFamily = async () => {
    if (!inviteCode.trim()) {
      alert('Inserisci un codice di invito valido.');
      return;
    }
    await handleJoinFamily(inviteCode, setFamilies, setUserFamily);
    setInviteCode('');
    setInviteCodeDialogOpen(false);
  };

  const handleLeaveCurrentFamily = async () => {
    if (userFamily && userFamily.id) {
      await handleLeaveFamily(userFamily.id, setUserFamily, setFamilies);
    }
  };

  // Funzione per aprire le preferenze di Iubenda
  const openIubendaPreferences = () => {
    if (
      window._iub &&
      window._iub.cs &&
      window._iub.cs.api &&
      typeof window._iub.cs.api.showBanner === 'function'
    ) {
      window._iub.cs.api.showBanner();
    } else {
      console.error("L'API di Iubenda non è disponibile.");
    }
  };

  // Ristrutturazione del menu nel Drawer con separatori
  const drawer = (
    <div>
      <Toolbar />
      <List>
        {/* Sezione: Navigazione Principale */}
        <ListItem button onClick={() => navigate('/dashboard')} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <DashboardIcon />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>
        <ListItem button onClick={() => navigate('/statistics')} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <BarChartIcon />
          </ListItemIcon>
          <ListItemText primary="Statistiche" />
        </ListItem>
        <ListItem
          button
          onClick={() => navigate('/expense-list', { state: { userFamily } })}
          sx={{ cursor: 'pointer' }}
        >
          <ListItemIcon>
            <ListAltIcon />
          </ListItemIcon>
          <ListItemText primary="Lista Spese" />
        </ListItem>
        <ListItem button onClick={() => setOpenPastExpense(true)} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <HistoryIcon />
          </ListItemIcon>
          <ListItemText primary="Spesa Retroattiva" />
        </ListItem>

        <Divider sx={{ my: 1 }} />

        {/* Sezione: Gestione Famiglia */}
        {userFamily ? (
          <ListItem button onClick={handleLeaveCurrentFamily} sx={{ cursor: 'pointer' }}>
            <ListItemIcon>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary="Esci dalla famiglia" />
          </ListItem>
        ) : (
          <>
            <ListItem button onClick={handleCreateNewFamily} sx={{ cursor: 'pointer' }}>
              <ListItemIcon>
                <AddCircleIcon />
              </ListItemIcon>
              <ListItemText primary="Crea una Famiglia" />
            </ListItem>
            <ListItem button onClick={() => setInviteCodeDialogOpen(true)} sx={{ cursor: 'pointer' }}>
              <ListItemIcon>
                <AddCircleIcon />
              </ListItemIcon>
              <ListItemText primary="Unisciti a una Famiglia" />
            </ListItem>
          </>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Sezione: Azioni Utili */}
        <ListItem button onClick={toggleMode} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
          </ListItemIcon>
          <ListItemText primary="Cambia Tema" />
        </ListItem>
        <ListItem button onClick={handleShowGuideOverlay} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <HelpOutlineIcon />
          </ListItemIcon>
          <ListItemText primary="Guida" />
        </ListItem>
        <ListItem button component="a" href="https://ko-fi.com/quantospendo" target="_blank" rel="noopener noreferrer" sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <FavoriteIcon />
          </ListItemIcon>
          <ListItemText primary="Dona" />
        </ListItem>
        <ListItem button onClick={() => setOpenMessageCenter(true)} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <MailIcon />
          </ListItemIcon>
          <ListItemText primary="Messaggi" />
        </ListItem>

        <Divider sx={{ my: 1 }} />

        {/* Sezione: Privacy e Cookie */}
        <ListItem button onClick={openIubendaPreferences} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <PolicyIcon />
          </ListItemIcon>
          <ListItemText primary="Gestione Cookie" />
        </ListItem>
        <ListItem button component="a" href="https://www.iubenda.com/privacy-policy/93756875" target="_blank" rel="noopener noreferrer" sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <SecurityIcon />
          </ListItemIcon>
          <ListItemText primary="Privacy Policy" />
        </ListItem>
        <ListItem button component="a" href="https://www.iubenda.com/privacy-policy/93756875/cookie-policy" target="_blank" rel="noopener noreferrer" sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <PolicyIcon />
          </ListItemIcon>
          <ListItemText primary="Cookie Policy" />
        </ListItem>
      </List>
      <AddPastExpense open={openPastExpense} onClose={() => setOpenPastExpense(false)} userFamily={userFamily} categories={categories} />
    </div>
  );

  if (loading) {
    return (
      <>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <CssBaseline />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </>
    );
  }

  const paperStyle = {
    p: 2,
    borderRadius: 0,
    border: '1px solid',
    borderColor: mode === 'light' ? '#000' : '#fff',
    boxShadow: 'none',
    backgroundColor: 'background.paper'
  };

  return (
    <>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        <AppBar position="fixed">
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="menu"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' }, cursor: 'pointer' }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Quantospendo
            </Typography>
            {userData && (
              <div>
                <IconButton
                  size="large"
                  edge="end"
                  aria-label="account utente"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenuOpen}
                  color="inherit"
                  sx={{ cursor: 'pointer' }}
                >
                  <Avatar alt={userData.name} src={userData.picture} />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  keepMounted
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      logoutHandler(navigate);
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <LogoutIcon sx={{ mr: 1 }} /> Logout
                  </MenuItem>
                </Menu>
              </div>
            )}
          </Toolbar>
        </AppBar>

        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
          aria-label="menu laterale"
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{ display: { xs: 'block', sm: 'none' } }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{ display: { xs: 'none', sm: 'block' } }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            paddingBottom: '56px',
            overflowY: 'auto'
          }}
        >
          <Toolbar />
          <Container maxWidth="lg">
            <Grid container spacing={3}>
              {/* Sezione Spese Incomplete */}
              <Grid item xs={12}>
                <Paper sx={paperStyle}>
                  <Typography variant="h6" gutterBottom>
                    Spese Incomplete
                  </Typography>
                  {incompleteExpenses.length === 0 ? (
                    <Typography>Nessuna spesa incompleta.</Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {incompleteExpenses.map((expense) => (
                        <Grid item xs={12} sm={6} key={expense.id}>
                          <Paper sx={paperStyle}>
                            <Typography><strong>Importo:</strong> €{expense.amount}</Typography>
                            <Typography><strong>Nome:</strong> {expense.name || 'Non specificato'}</Typography>
                            <Typography><strong>Categoria:</strong> {expense.category || 'Non specificata'}</Typography>
                            <Button
                              variant="contained"
                              color="secondary"
                              sx={{ mt: 2, cursor: 'pointer' }}
                              onClick={() => {
                                setSelectedExpense(expense);
                                setUpdatedName(expense.name);
                                setUpdatedCategory(expense.category);
                              }}
                            >
                              Completa i dettagli
                            </Button>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Paper>
              </Grid>

              {/* Sezione Spese Ricorrenti */}
              <Grid item xs={12}>
                <RecurringExpensesList
                  recurringExpenses={recurringExpenses}
                  onUpdate={updateRecurringExpense}
                  onDelete={deleteRecurringExpense}
                  userFamily={userFamily}
                />
              </Grid>

              {/* Sezione Gestione Famiglia */}
              <Grid item xs={12}>
                <Paper sx={paperStyle}>
                  <Typography variant="h6" gutterBottom>
                    Gestione Famiglia
                  </Typography>
                  {families.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddCircleIcon />}
                        onClick={handleCreateNewFamily}
                        sx={{ cursor: 'pointer' }}
                      >
                        Crea una Famiglia
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddCircleIcon />}
                        onClick={() => setInviteCodeDialogOpen(true)}
                        sx={{ cursor: 'pointer' }}
                      >
                        Unisciti a una Famiglia
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Codice di Invito: <strong>{families[0].invitationCode}</strong>
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigator.clipboard.writeText(families[0].invitationCode)}
                        sx={{ mb: 2, cursor: 'pointer' }}
                      >
                        Copia Codice
                      </Button>
                      <Typography variant="h6" gutterBottom>
                        Unisciti a una Famiglia
                      </Typography>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddCircleIcon />}
                        onClick={() => setInviteCodeDialogOpen(true)}
                        sx={{ cursor: 'pointer' }}
                      >
                        Inserisci Codice di Invito
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Dettagli Spesa Selezionata */}
              {selectedExpense && (
                <Grid item xs={12}>
                  <Paper sx={paperStyle}>
                    <Typography variant="h6" gutterBottom>
                      Completa i dettagli della spesa
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <TextField
                        label="Nome"
                        value={updatedName}
                        onChange={(e) => setUpdatedName(e.target.value)}
                      />
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <TextField
                        label="Categoria"
                        value={updatedCategory}
                        onChange={(e) => setUpdatedCategory(e.target.value)}
                      />
                    </FormControl>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleUpdateExpenseDetails}
                      sx={{ cursor: 'pointer' }}
                    >
                      Aggiorna Spesa
                    </Button>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Container>
        </Box>

        {/* Dialog per Aggiungere una Nuova Spesa */}
        <Dialog
          open={addExpenseOpen}
          onClose={() => setAddExpenseOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 0,
              border: '1px solid',
              borderColor: mode === 'light' ? '#000' : '#fff',
              backgroundColor: 'background.paper',
            },
          }}
        >
          <DialogTitle sx={{ color: 'text.primary' }}>Aggiungi Spesa</DialogTitle>
          <DialogContent>
            <AddExpense onClose={() => setAddExpenseOpen(false)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddExpenseOpen(false)} sx={{ cursor: 'pointer' }}>
              Chiudi
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog per Inserire il Codice di Invito */}
        <Dialog
          open={inviteCodeDialogOpen}
          onClose={() => setInviteCodeDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 0,
              border: '1px solid',
              borderColor: mode === 'light' ? '#000' : '#fff',
              backgroundColor: 'background.paper',
            },
          }}
        >
          <DialogTitle>Unisciti a una Famiglia</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <TextField
                label="Codice di Invito"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                fullWidth
              />
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInviteCodeDialogOpen(false)} sx={{ cursor: 'pointer' }}>
              Annulla
            </Button>
            <Button variant="contained" color="primary" onClick={handleJoinExistingFamily} sx={{ cursor: 'pointer' }}>
              Unisciti
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modale degli Achievement */}
        <Achievements open={achievementsOpen} onClose={handleCloseAchievements} />

        {/* Bottom Navigation */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
          }}
        >
          <BottomNavigation
            value={bottomNavValue}
            onChange={(event, newValue) => setBottomNavValue(newValue)}
            showLabels
            sx={{
              backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000',
              borderTop: '1px solid',
              borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
              boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.2)',
              width: '100%',
            }}
          >
            <BottomNavigationAction
              label="Dashboard"
              value="dashboard"
              icon={<DashboardIcon sx={{ color: 'text.primary' }} />}
              onClick={() => navigate('/dashboard')}
              sx={{ cursor: 'pointer', color: 'text.primary', '&.Mui-selected': { color: 'text.primary' } }}
            />
            <BottomNavigationAction
              label="Statistiche"
              value="statistics"
              icon={<BarChartIcon sx={{ color: 'text.primary' }} />}
              onClick={() => navigate('/statistics')}
              sx={{ cursor: 'pointer', color: 'text.primary', '&.Mui-selected': { color: 'text.primary' } }}
            />
            <BottomNavigationAction
              label="Spese"
              value="expense-list"
              icon={<ListAltIcon sx={{ color: 'text.primary' }} />}
              onClick={() => navigate('/expense-list', { state: { userFamily } })}
              sx={{ cursor: 'pointer', color: 'text.primary', '&.Mui-selected': { color: 'text.primary' } }}
            />
            <BottomNavigationAction
              label="Aggiungi"
              value="add-expense"
              icon={
                <Tooltip title="Aggiungi una nuova spesa" arrow placement="top" enterDelay={500}>
                  <AddCircleIcon sx={{ color: 'text.primary' }} />
                </Tooltip>
              }
              onClick={() => setAddExpenseOpen(true)}
              sx={{ cursor: 'pointer', color: 'text.primary', '&.Mui-selected': { color: 'text.primary' } }}
            />
          </BottomNavigation>
        </Box>
      </Box>

      <GuideOverlay open={showGuideOverlay} onClose={handleHideGuideOverlay} />
      <MessageCenter open={openMessageCenter} onClose={() => setOpenMessageCenter(false)} />
    </>
  );
};

export default Dashboard;
